import { useState } from "react";
import { motion } from "framer-motion";
import {
  Send,
  Mail,
  Phone,
  MapPin,
  Instagram,
  Twitter,
  Facebook,
  ShieldCheck,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import api from "../services/api";
import useApiData from "../hooks/useApiData";
import { Link } from "react-router-dom";

const fallbackData = {
  contact: {},
  social: {},
};

const contactIconMap = {
  email: Mail,
  phone: Phone,
  location: MapPin,
};

const socialIconMap = {
  instagram: Instagram,
  twitter: Twitter,
  facebook: Facebook,
};

/* ── reCAPTCHA-style badge ── */
function RecaptchaBadge() {
  return (
    <div
      className="flex items-center gap-3 border border-[var(--dvx-border)] bg-[var(--dvx-bg)] px-4 py-3"
      style={{ maxWidth: 260 }}
    >
      {/* Google shield logo (SVG) */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 64 72"
        width="36"
        height="40"
        aria-hidden="true"
      >
        <path
          d="M32 2L4 14v20c0 18.2 12.1 35.2 28 39.9C48 69.2 60 52.2 60 34V14L32 2z"
          fill="#4285F4"
        />
        <path d="M32 2L4 14v20c0 18.2 12.1 35.2 28 39.9V2z" fill="#3367D6" />
        <path
          d="M32 18c-7.7 0-14 6.3-14 14s6.3 14 14 14 14-6.3 14-14-6.3-14-14-14zm0 25c-6.1 0-11-4.9-11-11s4.9-11 11-11 11 4.9 11 11-4.9 11-11 11z"
          fill="#fff"
        />
        <circle cx="32" cy="32" r="7" fill="#fff" />
      </svg>

      {/* Text + links */}
      <div>
        <p className="text-xs font-bold text-[var(--dvx-text)] leading-tight">
          protected by reCAPTCHA
        </p>
        <p className="text-[10px] text-[var(--dvx-text-muted)] leading-tight mt-0.5">
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noreferrer"
            className="underline hover:text-[var(--dvx-primary)] transition-colors"
          >
            Privacy
          </a>
          {" · "}
          <a
            href="https://policies.google.com/terms"
            target="_blank"
            rel="noreferrer"
            className="underline hover:text-[var(--dvx-primary)] transition-colors"
          >
            Terms
          </a>
        </p>
      </div>

      {/* reCAPTCHA swirl icon */}
      <div className="ml-auto">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 64 64"
          width="32"
          height="32"
          aria-hidden="true"
        >
          <path
            d="M32 4C16.5 4 4 16.5 4 32s12.5 28 28 28 28-12.5 28-28S47.5 4 32 4zm0 50C19.3 54 9 43.7 9 31S19.3 8 32 8s23 10.3 23 23-10.3 23-23 23z"
            fill="#4285F4"
          />
          <path
            d="M32 14c-9.9 0-18 8.1-18 18 0 4.4 1.6 8.4 4.2 11.5l2.9-2.9C19.1 38.3 18 35.3 18 32c0-7.7 6.3-14 14-14 3.9 0 7.4 1.6 10 4.1l-4.1 4.1H50v-12l-4.1 4.1C42.8 15.8 37.6 14 32 14z"
            fill="#34A853"
          />
          <path
            d="M46 32c0 7.7-6.3 14-14 14-3.9 0-7.4-1.6-10-4.1l4.1-4.1H14v12l4.1-4.1C21.2 48.2 26.4 50 32 50c9.9 0 18-8.1 18-18h-4z"
            fill="#FBBC05"
          />
        </svg>
      </div>
    </div>
  );
}

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [agreedToComms, setAgreedToComms] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { data, loading, error } = useApiData("/contact-info", fallbackData);

  const contact = data?.contact || {};
  const social = data?.social || {};

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill all required fields");
      return;
    }

    if (!agreedToComms) {
      toast.error("Please agree to receive communications before submitting.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post("/contact", { ...formData, agreedToComms });

      toast.success(res?.data?.message || "Message sent successfully!");

      setFormData({ name: "", email: "", phone: "", message: "" });
      setAgreedToComms(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to send message. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Build contact info dynamically (hide empty)
  const contactInfo = [
    contact?.support_email && {
      type: "email",
      title: "Email",
      value: contact.support_email,
      href: `mailto:${contact.support_email}`,
    },
    contact?.support_phone && {
      type: "phone",
      title: "Phone",
      value: contact.support_phone,
      href: `tel:${contact.support_phone}`,
    },
    contact?.office_address && {
      type: "location",
      title: "Address",
      value: contact.office_address,
    },
  ].filter(Boolean);

  // Social links (hide empty)
  const socialLinks = Object.entries(social || {})
    .filter(([_, url]) => url)
    .map(([key, url]) => ({ platform: key, href: url, label: key }));

  return (
    <div
      id="contact"
      className="min-h-screen bg-[var(--dvx-bg)] pt-24 scroll-mt-24"
    >
      {/* <div className="mt-12 flex flex-col lg:flex-row items-stretch gap-6 px-10 mb-16">
        
        <div className="lg:w-[40%] flex items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl md:text-4xl font-oswald font-bold uppercase text-[var(--dvx-text)]">
              Apply for DVX coaching
            </h1>
            <p className="mt-4 text-[var(--dvx-text-muted)] max-w-xl mx-auto">
              Serious applicants only. If you qualify, you'll be contacted for a
              strategy call.
            </p>
          </motion.div>
        </div>

       
        <div className="lg:w-[60%] border border-[var(--dvx-border)] bg-[var(--dvx-surface)] py-3 px-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <h3 className="font-oswald text-lg font-bold uppercase text-[var(--dvx-text)] mb-1">
              Want an event-based transformation?
            </h3>
            <p className="text-sm text-[var(--dvx-text-muted)] max-w-md">
              Wedding / photoshoot / vacation timeline. Apply and we'll map a
              realistic plan with milestones.
            </p>
          </div>
          <Link to="/contact">
            <Button className="rounded-none px-6 py-3 bg-[var(--dvx-primary)] text-white font-oswald font-bold uppercase hover:opacity-90 transition">
              Apply with timeline
            </Button>
          </Link>
        </div>
      </div> */}

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h3 className="text-[var(--dvx-primary)] font-oswald text-xl font-semibold uppercase tracking-[0.20.em]">
              Contact
            </h3>

            <h1 className="mt-4 font-oswald text-4xl font-bold text-[var(--dvx-text)] uppercase tracking-tight">
              Apply for DVX coaching
            </h1>

            <p className="mt-4 text-[var(--dvx-text-muted)] font-manrope text-lg max-w-2xl mx-auto">
              Serious applicants only. If you qualify, you'll be contacted for a
              strategy call.
            </p>
          </motion.div>
        </div>
      </section>

      {/* MAIN */}
      <section className="pb-24 px-6">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-16">
          {/* INFO */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-8 order-2 lg:order-1"
          >
            {loading && <p>Loading contact info...</p>}
            {error && (
              <p className="text-red-500">Failed to load contact info</p>
            )}

            {!loading && contactInfo.length > 0 && (
              <div>
                <h3 className="text-xl font-oswald mb-4 uppercase">
                  Contact Info
                </h3>
                <div className="space-y-4">
                  {contactInfo.map((info, i) => {
                    const Icon = contactIconMap[info.type] || Mail;
                    return (
                      <div key={i} className="flex gap-4 items-center">
                        <Icon className="text-[var(--dvx-primary)]" />
                        <div>
                          <p className="text-sm text-[var(--dvx-text-muted)]">
                            {info.title}
                          </p>
                          {info.href ? (
                            <a href={info.href}>{info.value}</a>
                          ) : (
                            <span>{info.value}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {!loading && socialLinks.length > 0 && (
              <div>
                <h3 className="text-xl font-oswald mb-4 uppercase">
                  Follow Us
                </h3>
                <div className="flex gap-4">
                  {socialLinks.map((s, i) => {
                    const Icon = socialIconMap[s.platform] || Instagram;
                    return (
                      <a key={i} href={s.href} target="_blank" rel="noreferrer">
                        <Icon size={22} />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            {!loading && contact?.map_iframe && (
              <div
                className="mt-6"
                dangerouslySetInnerHTML={{ __html: contact.map_iframe }}
              />
            )}
          </motion.div>

          {/* FORM */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            className="order-1 lg:order-2"
          >
            <div className="bg-[var(--dvx-surface)] border p-8">
              <h2 className="text-2xl font-oswald mb-6 uppercase">
                Send Message
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label>Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                  />
                </div>

                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                  />
                </div>

                <div>
                  <Label>Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                  />
                </div>

                <div>
                  <Label>Message *</Label>
                  <Textarea
                    rows={5}
                    value={formData.message}
                    onChange={(e) =>
                      setFormData({ ...formData, message: e.target.value })
                    }
                  />
                </div>

                {/* ── Privacy declaration block ── */}
                <div className="space-y-4 border-t border-[var(--dvx-border)] pt-5">
                  {/* Privacy policy paragraph */}
                  <p className="text-xs text-[var(--dvx-text-muted)] leading-relaxed">
                    DVX is committed to protecting and respecting your privacy,
                    and we'll only use your personal information to administer
                    your account and to provide the products and services you
                    requested from us. From time to time, we would like to
                    contact you about our products and services, as well as
                    other content that may be of interest to you. If you consent
                    to us contacting you for this purpose, please tick below to
                    say how you would like us to contact you:
                  </p>

                  {/* Consent checkbox */}
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={agreedToComms}
                      onChange={(e) => setAgreedToComms(e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-[var(--dvx-primary)] cursor-pointer shrink-0"
                    />
                    <span className="text-xs text-[var(--dvx-text-muted)] leading-relaxed group-hover:text-[var(--dvx-text)] transition-colors">
                      I agree to receive other communications from DVX.
                    </span>
                  </label>

                  {/* Unsubscribe notice */}
                  {/* <p className="text-xs text-[var(--dvx-text-muted)] leading-relaxed">
                    You can unsubscribe from these communications at any time.
                    For more information on how to unsubscribe, our privacy
                    practices, and how we are committed to protecting and
                    respecting your privacy, please review our{" "}
                    <a
                      href="/privacy"
                      className="underline hover:text-[var(--dvx-primary)] transition-colors"
                    >
                      Privacy Policy
                    </a>
                    .
                  </p> */}

                  {/* Submit consent notice */}
                  <p className="text-xs text-[var(--dvx-text-muted)] leading-relaxed">
                    By clicking submit below, you consent to allow DVX to store
                    and process the personal information submitted above to
                    provide you the content requested.
                  </p>

                  {/* reCAPTCHA badge */}
                  <RecaptchaBadge />
                </div>

                {/* Submit button */}
                <Button
                  disabled={submitting || !agreedToComms}
                  className="w-full py-4 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                >
                  {submitting ? (
                    "Sending..."
                  ) : (
                    <>
                      <Send size={18} className="mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
